import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CAMPAIGN_PERSONAS, CAMPAIGN_STATUSES, CONSENT_TIERS } from "@demore/shared/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

type Campaign = {
  id: string;
  name: string;
  publicName: string | null;
  industry: string | null;
  status: string;
  consentTier: string;
  ownerUserId: string;
  // Audit V1
  persona?: string;
  defaultPlaylistId?: string | null;
  defaultWelcomeMessage?: string | null;
};

type PlaylistOption = { id: string; name: string };

export function CampaignsPage() {
  const { user, flags } = useAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Campaign | null>(null);
  const [managingMembers, setManagingMembers] = useState<Campaign | null>(null);
  const { data } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get<{ items: Campaign[] }>("/api/admin/campaigns"),
  });
  const { data: users } = useQuery({
    queryKey: ["users-for-campaigns"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; role: string }> }>("/api/admin/users"),
    enabled: user?.role === "super_admin",
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/campaigns/${id}`),
    onSuccess: () => {
      toast.success("Campaign deleted");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          "This campaign has active campaign-assigned videos — archive them (or move/re-tier) first.",
          { duration: 8000 },
        );
      } else {
        toast.error(err instanceof Error ? err.message : "Delete failed");
      }
    },
  });

  const canEdit = (c: Campaign) =>
    user?.role === "super_admin" || (user?.role === "campaign_owner" && c.ownerUserId === user.id);

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">Campaigns</h1>
        {(user?.role === "super_admin" || user?.role === "campaign_owner") && (
          <Button onClick={() => setCreateOpen(true)}>New campaign</Button>
        )}
      </div>
      {/* CEO Rev 1.0 discoverability: while workspaces coexist with the old
          global pages (strict cutover off), point people at the Open entry —
          two reviewers missed it. Disappears on its own once strict flips ON
          (the workspace becomes the only path, no callout needed). */}
      {flags.campaignWorkspaces && !flags.workspaceStrict && (
        <p className="text-sm text-gold bg-gold/10 border border-gold/30 rounded-md px-3 py-2">
          <span className="font-semibold">New — Campaign Workspaces:</span> click{" "}
          <span className="font-semibold">Open</span> on a campaign to enter its
          workspace (Overview, Media, Playlists, Surveys, Deliverables,
          Accomplishments, Reports, Team, Events).
        </p>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Industry</th>
              <th className="p-3 font-medium">Consent tier</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((c) => (
              <tr key={c.id} className="border-b border-admin-rule">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3">{c.industry ?? "—"}</td>
                <td className="p-3"><Badge variant="outline">{c.consentTier.replace(/_/g, " ")}</Badge></td>
                <td className="p-3"><Badge variant={c.status === "active" ? "ok" : "muted"}>{c.status}</Badge></td>
                <td className="p-3 text-right space-x-2">
                  {flags.campaignWorkspaces && (
                    <span className="inline-flex items-center gap-1.5">
                      {!flags.workspaceStrict && (
                        <Badge variant="ok" className="uppercase text-[10px]">New</Badge>
                      )}
                      <Button asChild size="sm">
                        <Link to={`/admin/campaigns/${c.id}/overview`}>Open</Link>
                      </Button>
                    </span>
                  )}
                  {canEdit(c) && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setManagingMembers(c)}>Members</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>Edit</Button>
                    </>
                  )}
                  {user?.role === "super_admin" && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => setPendingDelete(c)}>
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <CreateCampaignDialog open={createOpen} onOpenChange={setCreateOpen} users={users?.items ?? []} onSaved={() => qc.invalidateQueries({ queryKey: ["campaigns"] })} />
      <EditCampaignDialog campaign={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["campaigns"] })} />
      <MembersDialog campaign={managingMembers} onClose={() => setManagingMembers(null)} />
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete campaign "{pendingDelete?.name}"?</DialogTitle>
            <DialogDescription>
              This permanently removes the campaign. Videos in this campaign will have their
              campaign reference cleared; they are not deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingDelete && remove.mutate(pendingDelete.id)} disabled={remove.isPending}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditCampaignDialog({ campaign, onClose, onSaved }: { campaign: Campaign | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: campaign?.name ?? "",
    publicName: campaign?.publicName ?? "",
    industry: campaign?.industry ?? "",
    consentTier: campaign?.consentTier ?? "anonymized_only",
    status: campaign?.status ?? "active",
    persona: campaign?.persona ?? "general",
    defaultPlaylistId: campaign?.defaultPlaylistId ?? "",
    defaultWelcomeMessage: campaign?.defaultWelcomeMessage ?? "",
  });
  // Reset form when campaign changes
  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name,
        publicName: campaign.publicName ?? "",
        industry: campaign.industry ?? "",
        consentTier: campaign.consentTier,
        status: campaign.status,
        persona: campaign.persona ?? "general",
        defaultPlaylistId: campaign.defaultPlaylistId ?? "",
        defaultWelcomeMessage: campaign.defaultWelcomeMessage ?? "",
      });
    }
  }, [campaign]);

  const { data: playlistOpts } = useQuery({
    queryKey: ["playlists-options"],
    queryFn: () => api.get<{ items: PlaylistOption[] }>("/api/admin/playlists"),
    enabled: !!campaign,
  });

  const save = useMutation({
    mutationFn: () => api.patch(`/api/admin/campaigns/${campaign?.id}`, {
      name: form.name,
      publicName: form.publicName || undefined,
      industry: form.industry || undefined,
      consentTier: form.consentTier,
      status: form.status,
      persona: form.persona,
      defaultPlaylistId: form.defaultPlaylistId || null,
      defaultWelcomeMessage: form.defaultWelcomeMessage || null,
    }),
    onSuccess: () => { toast.success("Saved"); onSaved(); onClose(); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  if (!campaign) return null;
  return (
    <Dialog open={!!campaign} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit campaign</DialogTitle></DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Internal name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Public/anonymized name</Label><Input value={form.publicName} onChange={(e) => setForm((f) => ({ ...f, publicName: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Consent tier</Label>
                <Select value={form.consentTier} onChange={(e) => setForm((f) => ({ ...f, consentTier: e.target.value }))}>
                  {CONSENT_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {CAMPAIGN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
            {/* Audit V1: persona + default playlist + default welcome */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-admin-rule">
              <div className="space-y-1.5"><Label>Persona</Label>
                <Select value={form.persona} onChange={(e) => setForm((f) => ({ ...f, persona: e.target.value }))}>
                  {CAMPAIGN_PERSONAS.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Default playlist</Label>
                <Select value={form.defaultPlaylistId} onChange={(e) => setForm((f) => ({ ...f, defaultPlaylistId: e.target.value }))}>
                  <option value="">— none —</option>
                  {(playlistOpts?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Default welcome message</Label>
              <Textarea
                rows={3}
                value={form.defaultWelcomeMessage}
                onChange={(e) => setForm((f) => ({ ...f, defaultWelcomeMessage: e.target.value }))}
                placeholder="Auto-fills the welcome message when composing an issuance from this campaign…"
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateCampaignDialog({ open, onOpenChange, users, onSaved }: { open: boolean; onOpenChange: (v: boolean) => void; users: Array<{ id: string; name: string }>; onSaved: () => void }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    publicName: "",
    industry: "",
    ownerUserId: "",
    consentTier: "anonymized_only",
  });
  const save = useMutation({
    mutationFn: () => api.post("/api/admin/campaigns", {
      name: form.name,
      publicName: form.publicName || undefined,
      industry: form.industry || undefined,
      ownerUserId: user?.role === "campaign_owner" ? user.id : form.ownerUserId,
      consentTier: form.consentTier,
    }),
    onSuccess: () => { toast.success("Campaign created"); onSaved(); onOpenChange(false); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Internal name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Public/anonymized name</Label>
              <Input value={form.publicName} onChange={(e) => setForm((f) => ({ ...f, publicName: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Industry</Label>
                <Input value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Consent tier</Label>
                <Select value={form.consentTier} onChange={(e) => setForm((f) => ({ ...f, consentTier: e.target.value }))}>
                  {CONSENT_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
            </div>
            {user?.role === "super_admin" && (
              <div className="space-y-1.5">
                <Label>Owner</Label>
                <Select value={form.ownerUserId} onChange={(e) => setForm((f) => ({ ...f, ownerUserId: e.target.value }))}>
                  <option value="">Pick an owner</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending}>{save.isPending ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Members (campaign assignments) ────────────────────────────────────
function MembersDialog({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const { data } = useQuery({
    queryKey: ["campaign-members", campaign?.id],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; subjectType: "user" | "viewer"; subjectName: string | null; subjectEmail: string | null; isOwner?: boolean }> }>(
        `/api/admin/campaign-assignments/${campaign?.id}`,
      ),
    enabled: !!campaign,
  });
  const grant = useMutation({
    mutationFn: () => api.post(`/api/admin/campaign-assignments/${campaign?.id}`, { email }),
    onSuccess: () => {
      toast.success("Member added");
      setEmail("");
      qc.invalidateQueries({ queryKey: ["campaign-members", campaign?.id] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404) {
        // QA feedback (Saba, 2026-07-03): say what to DO, not just what's wrong.
        toast.error(
          "No user or past viewer with that email. Send them a Demo Pack first (opening it makes them a viewer), or invite them under Users.",
          { duration: 8000 },
        );
      } else if (err instanceof ApiError && err.status === 409) {
        toast.error("Already a member");
      } else {
        toast.error(err instanceof Error ? err.message : "Add failed");
      }
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/campaign-assignments/${campaign?.id}/${id}`),
    onSuccess: () => {
      toast.success("Member removed");
      qc.invalidateQueries({ queryKey: ["campaign-members", campaign?.id] });
    },
  });

  if (!campaign) return null;
  return (
    <Dialog open={!!campaign} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Members — {campaign.name}</DialogTitle>
          <DialogDescription>
            Grant a user or past viewer the highest content tier (campaign_assigned) for this campaign's videos.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") grant.mutate(); }}
              />
              <Button onClick={() => grant.mutate()} disabled={!email || grant.isPending}>
                {grant.isPending ? "Adding…" : "Add"}
              </Button>
            </div>
            <ul className="text-sm space-y-1">
              {(data?.items ?? []).map((m) => (
                <li key={m.id} className="flex items-center justify-between border-b border-admin-rule py-2">
                  <div>
                    <div className="font-medium">{m.subjectName ?? m.subjectEmail ?? "(unknown)"}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.subjectEmail} · {m.isOwner ? "owner" : m.subjectType}
                    </div>
                  </div>
                  {!m.isOwner && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => revoke.mutate(m.id)}>
                      Remove
                    </Button>
                  )}
                </li>
              ))}
              {(data?.items ?? []).length === 0 && (
                <li className="text-xs text-muted-foreground text-center py-4">No members assigned yet</li>
              )}
            </ul>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
